import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { isArray, isEmpty, isNil, size } from "lodash";
import { blue, green, grey, orange, red } from "@mui/material/colors";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { CACHED_DATA_REDUCER_TYPES } from "../../../../redux/reducers/cachedDataReducer";
import { useBorrowerPortalHook } from "../../../../hooks/useBorrowerPortalHook";
import { cachedDataItemSet } from "../../../../redux/actions/cachedDataActions";
import { formatPhone } from "../../../../_src_shared/utils/functions/formatPhone";
import { getDaysAgo } from "../../../../_src_shared/utils/functions/getDaysAgo";
import ResizeableSidebar from "../../../../components/sidebar/ResizeableSidebar";
import Flx from "../../../../components/layout/Flx";
import ChipItem from "../../../../components/ui/ChipItem";
import {
  AccountBalanceOutlined,
  AddOutlined,
  ArrowBackRounded,
  BadgeOutlined,
  BusinessOutlined,
  CalendarTodayOutlined,
  CheckOutlined,
  DescriptionOutlined,
  EditOutlined,
  EmailOutlined,
  FingerprintOutlined,
  HistoryOutlined,
  HomeOutlined,
  InfoOutlined,
  LocationOnOutlined,
  OpenInNewOutlined,
  PersonAddOutlined,
  PersonOutlined,
  PhoneOutlined,
  ScheduleSend,
  SearchOutlined,
  SendOutlined,
  SyncOutlined,
  UploadFileOutlined,
  VerifiedUserOutlined,
  WestOutlined,
} from "@mui/icons-material";
import TabSwitcher from "../../../../components/navigation/TabSwitcher";
import QuickActionButton from "./components/QuickActionButton";
import DetailSection from "../shared/DetailSection";

import TabContent from "../../../../components/navigation/TabContent";
import JsonPreview from "../../../../components/common/JsonPreview";
import BorrowerLoansTable from "./tables/BorrowerLoansTable";
import UserEmailAvatarRow from "../../../../components/ui/UserEmailAvatarRow";
import SectionDivider from "../shared/SectionDivider";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import DetailField from "../shared/DetailField";
import Txt from "../../../../components/typography/Txt";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { maskSSN } from "../../../../_src_shared/utils/functions/maskSSN";
import { formatDateShort } from "../../../../utils/dates/formatDateShort";
import AddEditBorrowerBankInfoForm from "../../../../forms/AddEditBorrowerBankInfoForm";
import AddEditBorrowerEntityForm from "../../../../forms/AddEditBorrowerEntityForm";
import AddEditBorrowerExperienceRecordForm from "../../../../forms/AddEditBorrowerExperienceRecordForm";
import EditBorrowerProfileData from "../../../../forms/EditBorrowerProfileData";
import EditBorrowerProfilePermittedUsers from "../../../../forms/EditBorrowerProfilePermittedUsers";
import EditBorrowerDocumentMetadataForm from "../../../../forms/EditBorrowerDocumentMetadataForm";
import { BORROWER_SIDEBAR_FORM_TYPES } from "../../constants/BORROWER_SIDEBAR_FORM_TYPES";
import Htag from "../../../../components/typography/Htag";

const BorrowerDetailsSidebarFormTypeRenderer = ({
  type,
  data,
  onClose,
  borrowerId,
  onSuccessFn,
}) => {
  const formMap = {
    [BORROWER_SIDEBAR_FORM_TYPES.ADD_BANK_ACCOUNT]: (
      <AddEditBorrowerBankInfoForm
        borrowerId={borrowerId}
        onSuccessFn={onSuccessFn}
        initialValues={data}
      />
    ),
    [BORROWER_SIDEBAR_FORM_TYPES.UPDATE_BANK_ACCOUNT]: (
      <AddEditBorrowerBankInfoForm
        borrowerId={borrowerId}
        onSuccessFn={onSuccessFn}
        initialValues={data}
      />
    ),
    [BORROWER_SIDEBAR_FORM_TYPES.ADD_ENTITY]: (
      <AddEditBorrowerEntityForm
        borrowerId={borrowerId}
        onSuccessFn={onSuccessFn}
        initialValues={data}
      />
    ),
    [BORROWER_SIDEBAR_FORM_TYPES.UPDATE_ENTITY]: (
      <AddEditBorrowerEntityForm
        borrowerId={borrowerId}
        onSuccessFn={onSuccessFn}
        initialValues={data}
      />
    ),
    [BORROWER_SIDEBAR_FORM_TYPES.ADD_EXPERIENCE]: (
      <AddEditBorrowerExperienceRecordForm
        borrowerId={borrowerId}
        onSuccessFn={onSuccessFn}
        initialValues={data}
      />
    ),
    [BORROWER_SIDEBAR_FORM_TYPES.UPDATE_EXPERIENCE]: (
      <AddEditBorrowerExperienceRecordForm
        borrowerId={borrowerId}
        onSuccessFn={onSuccessFn}
        initialValues={data}
      />
    ),
    [BORROWER_SIDEBAR_FORM_TYPES.UPDATE_PROFILE_DATA]: (
      <EditBorrowerProfileData
        borrowerId={borrowerId}
        onSuccessFn={onSuccessFn}
        initialValues={data}
      />
    ),
    [BORROWER_SIDEBAR_FORM_TYPES.UPDATE_PERMITTED_USERS]: (
      <EditBorrowerProfilePermittedUsers
        borrowerId={borrowerId}
        onSuccessFn={onSuccessFn}
        initialValues={data}
      />
    ),
    [BORROWER_SIDEBAR_FORM_TYPES.UPDATE_DOCUMENT_METADATA]: (
      <EditBorrowerDocumentMetadataForm
        borrowerId={borrowerId}
        onSuccessFn={onSuccessFn}
        file={data}
      />
    ),
  };

  return (
    <Flx column g={0}>
      <Flx
        ac
        sx={{
          height: 52,
          px: 2,
          // borderBottom: `1px solid ${grey[300]}`,
          background: "#fff",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Flx ac gap={1.5}>
          <Tooltip title="Back to borrower">
            <IconButton size="small" onClick={() => onClose(null)}>
              <ArrowBackRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
          <Flx ac g={1}>
            <Htag
              sx={{
                fontWeight: 500,
                fontSize: "20px",
                color: grey[800],
              }}
            >
              {type}
            </Htag>
            {/* <ChipItem
              value={type}
              sx={{
                span: {
                  fontWeight: 700,
                  fontSize: "16px !important",
                  color: grey[800],
                },
              }}
            /> */}
          </Flx>
        </Flx>
      </Flx>
      <Divider sx={{ borderColor: "#eeeeee" }} />
      <Box
        sx={{
          p: 3,
        }}
      >
        {formMap[type]}

        {/* <AddEditBorrowerEntityForm
          borrowerId={borrowerId}
          onSuccessFn={onSuccessFn}
          initialValues={data}
        /> */}
        {/* <AddEditBorrowerExperienceRecordForm
          borrowerId={borrowerId}
          onSuccessFn={onSuccessFn}
          initialValues={data}
        /> */}
      </Box>
    </Flx>
  );
};

export default BorrowerDetailsSidebarFormTypeRenderer;
